import {
  Button,
  makeStyles,
  mergeClasses,
  Spinner,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowSyncCircleFilled,
  ArrowSyncCircleRegular,
  bundleIcon,
} from "@fluentui/react-icons";
import { useContext } from "react";
import { NoDataCard } from "../../../components-v2/common/no-data-card/NoDataCard";
import { LayoutContext } from "../../../context/LayoutContext";
import { useApiResource } from "../../../hooks/useApiResource";
import { ForecastPayload } from "../../../models/weather/IWeatherForecast";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
} from "../../../utils/common/Constants";
import { buildNextHourHeaderLabels } from "../../../utils/weather/WeatherForecastHelper";
import GroupedMetricTable from "./GroupedMetricTable";
const baseURL = process.env.REACT_APP_API_BASE_URL;
const forecastEndPoint = process.env.REACT_APP_API_WEATHER_FORECAST;
const forecastBaseUrl = `${baseURL}${forecastEndPoint}`;

export interface IWeatherForecastProps {
  airport: string;
}

const WeatherForecast: React.FC<IWeatherForecastProps> = ({ airport }) => {
  const classes = useStyles();
  const SyncIcon = bundleIcon(ArrowSyncCircleFilled, ArrowSyncCircleRegular);

  const forecastUrl =
    forecastBaseUrl?.replace(AIRPORT_CODE_PARAMETER, airport) ?? "";

  const { data, isError, isFetching, refetch } =
    useApiResource<ForecastPayload>({
      url: forecastUrl,
      refetchOnWindowFocus: false,
      queryKey: ["weather-forecast", airport],
    });

  const { currentTime } = useContext(LayoutContext);

  const forecastData = data?.data || [];
  const slots = buildNextHourHeaderLabels(currentTime);

  const nodataCard = (
    <div style={{ margin: "auto" }}>
      <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />
    </div>
  );

  return (
    <section
      className={mergeClasses("amd-section-shadow mt-4", classes.root)}
      data-testid="weather-forecast"
    >
      <header
        className={mergeClasses(
          "amd-section-shadow__header",
          "amd-section-shadow__header--lg amd-container-hspacebtwn",
          classes.title
        )}
      >
        Forecast
        <Tooltip content="Refresh table" relationship="label">
          <Button
            appearance="transparent"
            aria-label="Refresh table"
            onClick={() => refetch()}
            data-testid="refresh-button"
          >
            <SyncIcon fontSize={24} />
          </Button>
        </Tooltip>
      </header>

      {isFetching && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={classes.containerHeight}
        />
      )}
      {isError && nodataCard}
      {!isError && !isFetching && forecastData?.length === 0 && nodataCard}
      {!isError && !isFetching && forecastData?.length > 0 && (
        <GroupedMetricTable timeSlots={slots} payload={forecastData} />
      )}
    </section>
  );
};

export default WeatherForecast;

const useStyles = makeStyles({
  root: {
    display: "grid",
    gridTemplateRows: "auto 1fr",
    gap: "8px",
    height: "100%",
    minHeight: "280px",
  },
  title: {
    paddingInline: "2px",
  },
  containerHeight: {
    minHeight: "548px",
  },
});
